import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../utils/AuthContext';
import { subscriptionAPI, coachingAPI } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  UserRound, Globe, Save, Loader2, Shield, Phone, CreditCard, 
  Calendar, Clock, MessageCircle, Sparkles, HelpCircle, Copy, Check,
  ArrowUpRight, Target, Megaphone, Users, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  { code: "US", name: "United States", flag: "🇺🇸" },
  { code: "CA", name: "Canada", flag: "🇨🇦" },
  { code: "UK", name: "United Kingdom", flag: "🇬🇧" },
  { code: "AU", name: "Australia", flag: "🇦🇺" },
  { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
  { code: "IE", name: "Ireland", flag: "🇮🇪" },
  { code: "DE", name: "Germany", flag: "🇩🇪" },
  { code: "FR", name: "France", flag: "🇫🇷" },
  { code: "ES", name: "Spain", flag: "🇪🇸" },
  { code: "IT", name: "Italy", flag: "🇮🇹" },
  { code: "NL", name: "Netherlands", flag: "🇳🇱" },
  { code: "SE", name: "Sweden", flag: "🇸🇪" },
  { code: "NO", name: "Norway", flag: "🇳🇴" },
  { code: "DK", name: "Denmark", flag: "🇩🇰" },
  { code: "CH", name: "Switzerland", flag: "🇨🇭" },
  { code: "IN", name: "India", flag: "🇮🇳" },
  { code: "SG", name: "Singapore", flag: "🇸🇬" },
  { code: "JP", name: "Japan", flag: "🇯🇵" },
  { code: "MX", name: "Mexico", flag: "🇲🇽" },
  { code: "BR", name: "Brazil", flag: "🇧🇷" },
  { code: "ZA", name: "South Africa", flag: "🇿🇦" },
  { code: "PH", name: "Philippines", flag: "🇵🇭" },
  { code: "OTHER", name: "Other", flag: "🌍" },
];

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern Time (US)" },
  { value: "America/Chicago", label: "Central Time (US)" },
  { value: "America/Denver", label: "Mountain Time (US)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US)" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "Pacific/Honolulu", label: "Hawaii Time" },
  { value: "America/Toronto", label: "Eastern Time (Canada)" },
  { value: "America/Vancouver", label: "Pacific Time (Canada)" },
  { value: "Europe/London", label: "London (GMT/BST)" },
  { value: "Europe/Paris", label: "Central European Time" },
  { value: "Europe/Berlin", label: "Berlin" },
  { value: "Europe/Amsterdam", label: "Amsterdam" },
  { value: "Australia/Sydney", label: "Sydney" },
  { value: "Australia/Melbourne", label: "Melbourne" },
  { value: "Australia/Perth", label: "Perth" },
  { value: "Pacific/Auckland", label: "Auckland (NZ)" },
  { value: "Asia/Tokyo", label: "Tokyo" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Dubai", label: "Dubai (GST)" },
];

const WHAT_BROUGHT_YOU_OPTIONS = [
  { value: "stress_anxiety", label: "Managing stress or anxiety" },
  { value: "career", label: "Career change or growth" },
  { value: "relationships", label: "Relationships" },
  { value: "self_discovery", label: "Self-discovery & personal growth" },
  { value: "life_transition", label: "Major life transition" },
  { value: "work_life_balance", label: "Work-life balance" },
  { value: "confidence", label: "Building confidence" },
  { value: "clarity", label: "Finding clarity & direction" },
  { value: "habits", label: "Building better habits" },
  { value: "other", label: "Something else" },
];

const REFERRAL_OPTIONS = [
  { value: "friend", label: "Friend or family" },
  { value: "social_media", label: "Social media" },
  { value: "google", label: "Google search" },
  { value: "podcast", label: "Podcast" },
  { value: "blog", label: "Blog or article" },
  { value: "coach_referral", label: "Another coach" },
  { value: "therapist_referral", label: "Therapist recommendation" },
  { value: "app_store", label: "App store" },
  { value: "other", label: "Other" },
];

const TIER_DISPLAY = {
  open_path: { name: "Open Path", price: "Free", color: "text-muted-foreground", bg: "bg-muted" },
  supported_path: { name: "Supported Path", price: "$9/mo", color: "text-blue-600", bg: "bg-blue-50" },
  guided_path: { name: "Guided Path", price: "$29/mo", color: "text-purple-600", bg: "bg-purple-50" },
  personal_path: { name: "Personal Path", price: "$99/mo", color: "text-amber-600", bg: "bg-amber-50" },
};

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [usageData, setUsageData] = useState(null);
  const [crisisResources, setCrisisResources] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [coachingApps, setCoachingApps] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    timezone: '',
    what_brought_you: '',
    referral_source: '',
    pronouns: '',
  });

  const loadUsageData = useCallback(async () => {
    try {
      const response = await subscriptionAPI.getUsage();
      setUsageData(response.data);
    } catch (error) {
      console.error('Failed to load usage data:', error);
    }
  }, []);

  const loadCrisisResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/crisis-resources`);
      setCrisisResources(response.data);
    } catch (error) {
      console.error('Failed to load crisis resources:', error);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`);
      setAllUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadCoachingApps = useCallback(async () => {
    setAppsLoading(true);
    try {
      const response = await coachingAPI.getApplications();
      setCoachingApps(response.data);
    } catch (error) {
      console.error('Failed to load coaching applications:', error);
    } finally {
      setAppsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        country: user.country || 'US',
        timezone: user.timezone || '',
        what_brought_you: user.what_brought_you || '',
        referral_source: user.referral_source || '',
        pronouns: user.pronouns || '',
      });
      loadUsageData();
      loadCrisisResources();
      if (user.is_admin) {
        loadUsers();
        loadCoachingApps();
      }
    }
  }, [user, loadUsageData, loadCrisisResources, loadUsers, loadCoachingApps]);

  const updateUserTier = async (targetUserId, newTier) => {
    try {
      await axios.patch(`${API}/admin/users/${targetUserId}/tier?tier=${newTier}`);
      toast.success('User tier updated');
      loadUsers();
    } catch (error) {
      toast.error('Failed to update tier');
    }
  };

  const deleteUser = async (targetUserId, email) => {
    if (!window.confirm(`Remove ${email}? This will delete their account and all their data.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${targetUserId}`);
      toast.success('User removed');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove user');
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/auth/profile`, formData);
      toast.success('Profile saved');
      if (refreshUser) {
        await refreshUser();
      }
      await loadCrisisResources();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const copyAccountId = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      setCopiedId(true);
      toast.success('Account ID copied');
      setTimeout(() => setCopiedId(false), 2000);
    }
  };

  const getCountryDisplay = (code) => {
    const c = COUNTRIES.find(c => c.code === code);
    return c ? `${c.flag} ${c.name}` : code;
  };

  const tierInfo = TIER_DISPLAY[user?.subscription_tier] || TIER_DISPLAY.open_path;
  const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { 
    month: 'long', year: 'numeric' 
  }) : '';

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-serif mb-2">Profile</h1>
          <p className="text-muted-foreground">Manage your account and preferences</p>
        </div>

        {/* Subscription Info */}
        <Card className="p-6 rounded-2xl" data-testid="subscription-info">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <CreditCard className="text-primary" size={20} />
            </div>
            <div>
              <h2 className="font-serif text-lg">Subscription</h2>
              <p className="text-sm text-muted-foreground">Your current plan and usage</p>
            </div>
          </div>

          <div className="space-y-4">
            {/* Current Plan */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30">
              <div className="flex items-center gap-3">
                <Sparkles className={tierInfo.color} size={20} />
                <div>
                  <p className="font-medium">{tierInfo.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.subscription_status === 'active' ? 'Active' : user?.subscription_status}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-muted-foreground">{tierInfo.price}</span>
                <span className={`text-xs px-3 py-1 rounded-full ${tierInfo.bg} ${tierInfo.color}`}>
                  Current Plan
                </span>
              </div>
            </div>

            {/* AI Messages Usage - Only show for Open Path (limited tier) */}
            {usageData && user?.subscription_tier === 'open_path' && (
              <div className="p-4 rounded-xl bg-muted/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageCircle size={16} className="text-muted-foreground" />
                    <span className="text-sm">AI Messages This Month</span>
                  </div>
                  <span className="text-sm font-medium">
                    {usageData.ai_messages_used} / {usageData.ai_messages_limit}
                  </span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all"
                    style={{ width: `${Math.min((usageData.ai_messages_used / usageData.ai_messages_limit) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            {/* Unlimited Messages indicator for paid tiers */}
            {usageData && user?.subscription_tier !== 'open_path' && (
              <div className="p-4 rounded-xl bg-muted/30">
                <div className="flex items-center gap-2">
                  <MessageCircle size={16} className="text-teal-600" />
                  <span className="text-sm">Unlimited AI Messages</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Included</span>
                </div>
              </div>
            )}

            {/* Renewal / Upgrade */}
            {user?.subscription_expires_at && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Calendar size={14} />
                <span>Renews {new Date(user.subscription_expires_at).toLocaleDateString()}</span>
              </div>
            )}

            {user?.subscription_tier !== 'personal_path' && (
              <Button 
                variant="outline" 
                onClick={() => navigate('/pricing')}
                className="w-full rounded-full"
              >
                <ArrowUpRight size={16} className="mr-2" />
                View Plans & Upgrade
              </Button>
            )}
          </div>
        </Card>

        {/* Profile Info */}
        <Card className="p-6 rounded-2xl" data-testid="profile-info">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
              <UserRound className="text-teal-600" size={20} />
            </div>
            <div>
              <h2 className="font-serif text-lg">Personal Info</h2>
              <p className="text-sm text-muted-foreground">How we know you</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1.5 rounded-xl"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label htmlFor="pronouns">Pronouns (optional)</Label>
                <Input
                  id="pronouns"
                  value={formData.pronouns}
                  onChange={(e) => setFormData({ ...formData, pronouns: e.target.value })}
                  placeholder="e.g., she/her, he/him, they/them"
                  className="mt-1.5 rounded-xl"
                  data-testid="pronouns-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                value={user?.email || ''}
                disabled
                className="mt-1.5 rounded-xl bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
            </div>
          </div>
        </Card>

        {/* Location & Time */}
        <Card className="p-6 rounded-2xl" data-testid="location-settings">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Globe className="text-emerald-600" size={20} />
            </div>
            <div>
              <h2 className="font-serif text-lg">Location & Time</h2>
              <p className="text-sm text-muted-foreground">For localized resources and scheduling</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="country">Country</Label>
              <Select 
                value={formData.country} 
                onValueChange={(v) => setFormData({ ...formData, country: v })}
              >
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="country-select">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="timezone">Time Zone</Label>
              <Select 
                value={formData.timezone} 
                onValueChange={(v) => setFormData({ ...formData, timezone: v })}
              >
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="timezone-select">
                  <SelectValue placeholder="Select timezone" />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz.value} value={tz.value}>
                      {tz.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* About You (for analytics & better experience) */}
        <Card className="p-6 rounded-2xl" data-testid="about-you">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Target className="text-purple-600" size={20} />
            </div>
            <div>
              <h2 className="font-serif text-lg">About Your Journey</h2>
              <p className="text-sm text-muted-foreground">Help us understand how to serve you better</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="what_brought_you">What brought you to ClarityPath?</Label>
              <Select 
                value={formData.what_brought_you} 
                onValueChange={(v) => setFormData({ ...formData, what_brought_you: v })}
              >
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="what-brought-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {WHAT_BROUGHT_YOU_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="referral_source">How did you hear about us?</Label>
              <Select 
                value={formData.referral_source} 
                onValueChange={(v) => setFormData({ ...formData, referral_source: v })}
              >
                <SelectTrigger className="mt-1.5 rounded-xl" data-testid="referral-select">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
                <SelectContent>
                  {REFERRAL_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Crisis Resources */}
        {crisisResources && (
          <Card className="p-6 rounded-2xl border-amber-200 bg-amber-50/50" data-testid="crisis-resources">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <Shield className="text-amber-600" size={20} />
              </div>
              <div>
                <h2 className="font-serif text-lg">Crisis Resources</h2>
                <p className="text-sm text-muted-foreground">
                  For {getCountryDisplay(crisisResources.country)} — Clair knows these numbers
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                <Phone size={16} className="text-red-500 flex-shrink-0" />
                <div>
                  <p className="font-medium">Emergency</p>
                  <p className="text-muted-foreground">{crisisResources.resources.emergency}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl">
                <Phone size={16} className="text-blue-500 flex-shrink-0" />
                <div>
                  <p className="font-medium">Crisis Line</p>
                  <p className="text-muted-foreground text-xs">{crisisResources.resources.crisis_line}</p>
                </div>
              </div>
              {crisisResources.resources.dv_hotline && (
                <div className="flex items-center gap-3 p-3 bg-white/70 rounded-xl md:col-span-2">
                  <Phone size={16} className="text-purple-500 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Domestic Violence Hotline</p>
                    <p className="text-muted-foreground text-xs">{crisisResources.resources.dv_hotline}</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Account Info (for support) */}
        <Card className="p-6 rounded-2xl bg-muted/30" data-testid="account-info">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <HelpCircle className="text-muted-foreground" size={20} />
            </div>
            <div>
              <h2 className="font-serif text-lg">Account Info</h2>
              <p className="text-sm text-muted-foreground">For customer support</p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Member since</span>
              <span className="font-medium">{memberSince}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Account ID</span>
              <button 
                onClick={copyAccountId}
                className="flex items-center gap-2 font-mono text-xs bg-background px-3 py-1.5 rounded-lg hover:bg-muted transition-colors"
              >
                {user?.id?.slice(0, 8)}...
                {copiedId ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        </Card>

        {/* Admin: Manage Users */}
        {user?.is_admin && (
          <Card className="p-6 space-y-4" data-testid="manage-users-section">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Users size={20} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-serif">Manage Users</h2>
                <p className="text-sm text-muted-foreground">Update subscription tiers for testers and users</p>
              </div>
            </div>
            
            {usersLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <Loader2 size={16} className="animate-spin" /> Loading users...
              </div>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="text-left px-4 py-3 font-medium">User</th>
                      <th className="text-left px-4 py-3 font-medium">Current Tier</th>
                      <th className="text-left px-4 py-3 font-medium">Change Tier</th>
                      <th className="text-right px-4 py-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {allUsers.map((u) => {
                      const tierInfo = TIER_DISPLAY[u.subscription_tier] || TIER_DISPLAY.open_path;
                      return (
                        <tr key={u.id} className="border-b border-border last:border-0" data-testid={`user-row-${u.id}`}>
                          <td className="px-4 py-3">
                            <div>
                              <p className="font-medium">{u.name || 'No name'}</p>
                              <p className="text-xs text-muted-foreground">{u.email}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-1 rounded-full ${tierInfo.bg} ${tierInfo.color} font-medium`}>
                              {tierInfo.name}
                            </span>
                            {u.is_admin && <span className="ml-2 text-xs text-muted-foreground">(Admin)</span>}
                          </td>
                          <td className="px-4 py-3">
                            <select
                              value={u.subscription_tier || 'open_path'}
                              onChange={(e) => updateUserTier(u.id, e.target.value)}
                              className="px-2 py-1.5 border border-border rounded-lg bg-background text-xs"
                              data-testid={`tier-select-${u.id}`}
                            >
                              <option value="open_path">Open Path (Free)</option>
                              <option value="supported_path">Supported Path</option>
                              <option value="guided_path">Guided Path</option>
                              <option value="personal_path">Personal Path</option>
                            </select>
                          </td>
                          <td className="px-4 py-3 text-right">
                            {u.id !== user?.id && (
                              <button
                                onClick={() => deleteUser(u.id, u.email)}
                                className="text-xs text-red-500 hover:text-red-700 hover:underline"
                                data-testid={`delete-user-${u.id}`}
                              >
                                Remove
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {allUsers.length === 0 && (
                  <p className="text-center text-muted-foreground py-6 text-sm">No users found</p>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Admin: Coaching Applications */}
        {user?.is_admin && (
          <Card className="p-6 space-y-4" data-testid="coaching-applications-section">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <FileText size={20} className="text-accent" />
              </div>
              <div>
                <h2 className="text-lg font-serif">Coaching Applications</h2>
                <p className="text-sm text-muted-foreground">Review private coaching applications</p>
              </div>
            </div>
            
            {appsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <Loader2 size={16} className="animate-spin" /> Loading applications...
              </div>
            ) : coachingApps.length === 0 ? (
              <p className="text-center text-muted-foreground py-6 text-sm">No applications yet</p>
            ) : (
              <div className="space-y-3">
                {coachingApps.map((app) => (
                  <div key={app.id} className="border border-border rounded-xl p-4 space-y-2" data-testid={`coaching-app-${app.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{app.name}</p>
                        <p className="text-xs text-muted-foreground">{app.email}</p>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        app.status === 'approved' ? 'bg-green-100 text-green-700' :
                        app.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {app.status}
                      </span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                      <div><span className="font-medium text-foreground">Goals:</span> {app.goals}</div>
                      <div><span className="font-medium text-foreground">What brought them:</span> {app.what_brought_you}</div>
                      <div><span className="font-medium text-foreground">Availability:</span> {app.availability?.replace(/_/g, ' ')}</div>
                      <div><span className="font-medium text-foreground">Budget:</span> {app.budget_expectations?.replace(/_/g, ' ')}</div>
                    </div>
                    {app.additional_info && (
                      <p className="text-sm text-muted-foreground"><span className="font-medium text-foreground">Additional:</span> {app.additional_info}</p>
                    )}
                    <div className="text-xs text-muted-foreground">Applied: {new Date(app.created_at).toLocaleDateString()}</div>
                    {app.status === 'pending' && (
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          size="sm"
                          className="rounded-full text-xs h-7"
                          onClick={async () => {
                            try {
                              await coachingAPI.updateApplication(app.id, 'approved');
                              toast.success('Application approved');
                              loadCoachingApps();
                            } catch (error) {
                              toast.error('Failed to update application');
                            }
                          }}
                          data-testid={`approve-app-${app.id}`}
                        >
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs h-7"
                          onClick={async () => {
                            try {
                              await coachingAPI.updateApplication(app.id, 'rejected');
                              toast.success('Application declined');
                              loadCoachingApps();
                            } catch (error) {
                              toast.error('Failed to update application');
                            }
                          }}
                          data-testid={`reject-app-${app.id}`}
                        >
                          Decline
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end pb-8">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="rounded-full"
            data-testid="save-profile-btn"
          >
            {loading ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Save className="mr-2" size={16} />
            )}
            Save Changes
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
